import { Button, Tooltip } from "@eworkit/iris";
import React, { FC, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { fetchPublicProfileFollowStatus, followPublicProfile, unfollowPublicProfile } from "@/data/public-profiles";
import { useFetcher } from "@/hooks/http";
import { useToasts } from "@/views/components/Toast/useToasts";

export interface IFollowButtonProps {
  publicProfileId: number;
  onUpdate?: () => void;
  className?: string;
}

export const FollowButton: FC<IFollowButtonProps> = ({ publicProfileId, onUpdate, className }) => {
  const { t } = useTranslation("session");
  const { contextCompany } = useContext(UserContext);
  const { addToast } = useToasts();

  const statusFetcher = useFetcher(fetchPublicProfileFollowStatus, { publicProfileId, companyId: contextCompany?.id });

  const handleFollowClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        await followPublicProfile({ publicProfileId, companyId: contextCompany?.id });
        statusFetcher.fetch();
        onUpdate?.();
      } catch (e) {
        addToast({
          text: t("common:VALIDATION_MESSAGES.GENERAL"),
          type: "warning",
        });
      }
    },
    [publicProfileId],
  );

  const handleUnfollowClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        await unfollowPublicProfile({ publicProfileId, companyId: contextCompany?.id });
        statusFetcher.fetch();
        onUpdate?.();
      } catch (e) {
        addToast({
          text: t("common:VALIDATION_MESSAGES.GENERAL"),
          type: "warning",
        });
      }
    },
    [publicProfileId],
  );

  if (!statusFetcher.payload) {
    return null;
  }

  return (
    <>
      {statusFetcher.payload === "FOLLOWED" ? (
        <Tooltip content={t("CLIENT_PAGE.UNFOLLOW_BUTTON_TOOLTIP")} placement="top">
          {tooltipProps => (
            <Button variant="secondary" onClick={handleUnfollowClick} className={className} {...tooltipProps}>
              <Text size="lg">{t("CLIENT_PAGE.UNFOLLOW_BUTTON")}</Text>
            </Button>
          )}
        </Tooltip>
      ) : (
        <Button variant="primary" onClick={handleFollowClick} className={className}>
          <Text size="lg">{t("CLIENT_PAGE.FOLLOW_BUTTON")}</Text>
        </Button>
      )}
    </>
  );
};
